import { forwardRef } from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'sm' | 'md' | 'lg';
  elevation?: 'flat' | 'raised';
}

export interface CardSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const paddingClasses = {
  none: '',
  sm: 'p-3',
  md: 'p-card-pad',
  lg: 'p-6',
} as const;

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

const CardRoot = forwardRef<HTMLDivElement, CardProps>(function Card(
  { padding = 'md', elevation = 'flat', className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl bg-[color:var(--color-surface)] text-[color:var(--color-text-primary)]',
        elevation === 'raised'
          ? 'shadow-card bg-[color:var(--color-surface-raised)]'
          : 'shadow-sm',
        paddingClasses[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

export const CardHeader = forwardRef<HTMLDivElement, CardSectionProps>(function CardHeader(
  { padding = 'md', className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'border-b border-[color:var(--color-border-subtle)]',
        padding === 'md' ? 'p-card-pad pb-3' : paddingClasses[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

export const CardBody = forwardRef<HTMLDivElement, CardSectionProps>(function CardBody(
  { padding = 'md', className, children, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn(paddingClasses[padding], className)} {...rest}>
      {children}
    </div>
  );
});

export const CardFooter = forwardRef<HTMLDivElement, CardSectionProps>(function CardFooter(
  { padding = 'md', className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'border-t border-[color:var(--color-border-subtle)]',
        padding === 'md' ? 'p-card-pad pt-3' : paddingClasses[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
});
